import _ from 'underscore';

import { zeigeKleineNachricht } from '../systemNachricht.js';

class StammdatenService {
	constructor() {
		this._sort = 0;
		this.postenListe = [];
		this.einsatzortListe = [];
		this.mitarbeiter = [];
		this.qualifikationen = [];
		this.unternehmen = undefined;
		// wird beim Start in index.js:fuelleBetriebSelect gesetzt
		this.aktuelleBS = undefined;
	}

	get sort() {
		return this._sort;
	}

	set sort(sortierung) {
		this._sort = sortierung;
	}

	get aktuelleBetriebsstaette() {
		return this.aktuelleBS;
	}

	set aktuelleBetriebsstaette(betriebID) {
		this.aktuelleBS = betriebID;
	}

	/**
	 * Gibt uns den Wert zurück ob die Qualifikationen in der ausgewählten Betriebsstätte ausgeblendet werden müssen.
	 * - DienstplanQualifikationenAusblenden
	 * @returns true/false
	 */
	async getQualifikationenAusblenden() {
		if (!this.unternehmen) {
			await this.getUnternehmensdaten();
		}
		const betrieb = this.unternehmen.Betriebsstaette.find((bs) => _.isEqual(bs._id, this.aktuelleBS));
		if (_.isEmpty(betrieb)) {
			return false;
		}
		return betrieb.DienstplanQualifikationenAusblenden;
	}

	async getUnternehmensdaten() {
		// aktualisieren nur, falls das Unternehmen noch undefined ist
        if (!this.unternehmen) {
            try {
                const response = await fetch('/neolohn/api/unternehmen');
                if (response.ok) {
                    const result = await response.json();
                    this.unternehmen = result;
                }
            } catch (error) {
                console.log(error);
                return false;
            }
        }
        return this.unternehmen;
	}

	/**
	 * Ruft alle Mitarbeiter der aktuellen Betriebsstätte vom dem Server auf
	 * @param {boolean} refresh manchmal wollen wir die Daten Neuladen, z.B. bei Betriebsstättenwechsel
	 * @returns Serverantwort
	 */
	async getMitarbeiter(refresh = false) {
		if (!refresh && this.mitarbeiter.length > 0) {
			this.sortiereMitarbeiter();
			return this.mitarbeiter;
		}
		try {
			const response = await fetch(`/dienstplan/api/mitarbeiter?BetriebsstaetteID=${this.aktuelleBS}`);
			if (response.ok) {
				const result = await response.json();
				this.mitarbeiter = result;
				this.sortiereMitarbeiter();
				return this.mitarbeiter;
			}
			return null;
		} catch (error) {
			console.log(error);
			throw error;
		}
	}

	/**
	 * Sortiert die Mitarbeiterliste nach der Toolbar-Sortierung (this._sort):
	 * 0 = Sortierreihenfolge aus den Stammdaten (dann Name), ±1 = Name auf-/absteigend,
	 * ±2 = Personalnummer auf-/absteigend (numerisch, ohne Personalnummer ans Ende, dann Name).
	 */
	sortiereMitarbeiter() {
		const name = (m) => `${m.Personalien.Nachname}${m.Personalien.Vorname}`;
		const nachName = (a, b) => name(a).localeCompare(name(b));
		const pnr = (m) => m.Personalien.Personalnummer;
		const hatPnr = (m) => pnr(m) !== undefined && pnr(m) !== null && pnr(m) !== '';
		switch (this._sort) {
			case -1:
				this.mitarbeiter.sort(nachName);
				break;
			case 1:
				this.mitarbeiter.sort((a, b) => nachName(b, a));
				break;
			case 2:
			case -2: {
				const richtung = this._sort === 2 ? 1 : -1;
				this.mitarbeiter.sort((a, b) => {
					if (hatPnr(a) !== hatPnr(b)) return hatPnr(a) ? -1 : 1;
					const vergleich = hatPnr(a) ? String(pnr(a)).localeCompare(String(pnr(b)), 'de', { numeric: true }) : 0;
					return (richtung * vergleich) || nachName(a, b);
				});
				break;
			}
			default:
				// Wir sortieren erst nach der vordefinierten Sortierreihenfolge und dann nach dem Nachnamen
				this.mitarbeiter.sort((a, b) => a.Personalien.Sortierreihenfolge - b.Personalien.Sortierreihenfolge || nachName(a, b));
				break;
		}
	}

	/**
	 * Ruft alle Qualifikationen von dem Server auf
	 * @returns Serverantwort
	 */
	async getQualifikationen() {
		if (this.qualifikationen.length > 0) {
			return this.qualifikationen;
		}
		try {
			const response = await fetch('/dienstplan/api/qualifikationen');
			if (response.ok) {
				const result = await response.json();
				this.qualifikationen = result;
				return result;
			}
			return null;
		} catch (error) {
			console.log(error);
			return null;
		}
	}

	/**
	 * Ruft alle Posten der aktuellen Betriebsstätte von dem Server auf
	 * @param {boolean} refresh manchmal wollen wir die Daten Neuladen, z.B. bei Betriebsstättenwechsel
	 * @returns Serverantwort
	 */
	async getPosten(refresh = false) {
		if (!refresh && this.postenListe.length > 0) {
			return this.postenListe;
		}
		try {
			const response = await fetch(`/dienstplan/api/posten?BetriebsstaetteID=${this.aktuelleBS}`);
			if (response.ok) {
				const result = await response.json();
				// nur verwendete Posten anzeigen...
				this.postenListe = result.filter((p) => p.Verwenden);
				this.postenListe.sort((a, b) => a.Posten.localeCompare(b.Posten));
				return this.postenListe;
			}
			return null;
		} catch (error) {
			console.log(error);
			return null;
		}
	}

	/**
	 * Ruft alle Einsatzorte der aktuellen Betriebsstätte vom dem Server auf
	 * @param {boolean} refresh manchmal wollen wir die Daten Neuladen, z.B. bei Betriebsstättenwechsel
	 * @returns Serverantwort
	 */
	async getEinsatzorte(refresh = false) {
		if (!refresh && this.einsatzortListe.length > 0) {
			return this.einsatzortListe;
		}
		try {
			const response = await fetch(`/dienstplan/api/einsatzorte?BetriebsstaetteID=${this.aktuelleBS}`);
			if (response.ok) {
				const result = await response.json();
				// nur verwendete Einsatzorte anzeigen...
				this.einsatzortListe = result.filter((e) => e.Verwenden);
				this.einsatzortListe.sort((a, b) => a.Bezeichnung.localeCompare(b.Bezeichnung));
				return this.einsatzortListe;
			}
			return null;
		} catch (error) {
			console.log(error);
			return null;
		}
	}

	/**
	 * Ruft alle Dienstplan Tagesnotizen der Woche auf.
	 * @param {DateString} datumVon
	 * @param {DateString} datumBis
	 * @param {string} betriebsstaetteID
	 * @returns array der Tagesnotizen
	 */
	async getTagesnotizenWoche(datumVon, datumBis, betriebsstaetteID) {
		try {
			const urlParams = new URLSearchParams({ DatumVon: datumVon, DatumBis: datumBis, BetriebsstaetteID: betriebsstaetteID });
			const response = await fetch(`/dienstplan/api/tagesnotiz?${urlParams.toString()}`);
			if (response.ok) {
				const notizen = await response.json();
				return notizen;
			}
			zeigeKleineNachricht('Fehler beim Laden der Tagesnotizen!', -1);
			return [];
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Fehler beim Laden der Tagesnotizen!', -1);
			return [];
		}
	}

	/**
	 * Fetch PUT Request für das Updaten einer Tagesnotiz
	 * @param {string} notiz
	 * @param {DateString} datum
	 * @param {string} betriebsstaetteID
	 */
	async fetchUpdateTagesNotiz(notiz, datum, betriebsstaetteID) {
		try {
			const body = {
				Notiz: notiz,
				Datum: datum,
				BetriebsstaetteID: betriebsstaetteID
			};
			const response = await fetch('/dienstplan/api/tagesnotiz', {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(body)
			});
			if (response.ok) {
				zeigeKleineNachricht('Speichern erfolgreich!', 1);
				return true;
			}
			zeigeKleineNachricht('Speichern fehlgeschlagen!', -1);
			return false;
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Speichern fehlgeschlagen!', -1);
			return false;
		}
	}
}

const stammdatenService = new StammdatenService();
export { stammdatenService };
